/**
 * "You're in — but nothing will run yet, and here is the one thing to do."
 *
 * ─── THE STATE THIS EXISTS FOR ────────────────────────────────────────────
 *
 * A person accepts an invitation, signs in, and sees a Workspace with boards,
 * cards and projects in it. Everything looks available. Then they open a card,
 * type a message, and the turn fails — because running an agent spends an AI
 * subscription and they have not connected one. Under
 * `PER_ACCOUNT_CLI_CREDENTIALS_ENABLED` (production, since 2026-08-01) that
 * refusal is `execution_lane_required`, reason `no_vendor_login`.
 *
 * The refusal itself is correct and is not what this component fixes. What it
 * fixes is WHEN they learn it. A refusal that only arrives on the first turn
 * teaches a new person that the product is broken, in the ninety seconds where
 * they have the least evidence to think otherwise. This says it on arrival, on
 * every authenticated screen, with the route that clears it.
 *
 * ─── WHY THE JUDGE IS THE SAME ONE THE SPAWN USES ─────────────────────────
 *
 * `GET /api/vendors/status` is the endpoint `org-auth.ts::executionLaneOutcome`
 * consults through `getVendorStatuses()` before it allows a run — the same
 * function, resolved through the same per-account tree, for the same person.
 * So this banner and the gate cannot disagree: if any row is `ready` the gate
 * would pass and the banner does not render; if none is, the gate refuses and
 * the banner is up. A second definition of "has a usable login" is exactly how
 * `vendors.ts`'s own header records this surface breaking before — a green
 * "Claude ready" over an empty tree, followed by a refusal the interface had
 * just promised was impossible.
 *
 * ─── A NO-OP FOR THE FOUNDER, BY CONSTRUCTION ─────────────────────────────
 *
 * His tree has held completed logins since the box was built, so `ready` is
 * true and this renders `null` — zero footprint on the only person using the
 * instance today. It is not gated on a role or on "is this an invitee": that
 * would need a definition of invitee, and every definition of it drifts (see
 * the long note in `org-auth.ts` on why the rule is "has a lane" and not "is a
 * guest"). It lifts by itself the moment a login completes.
 *
 * ─── NOT DISMISSIBLE ──────────────────────────────────────────────────────
 *
 * `ClaudeAuthBanner` lets you hide it for an hour, and should: it reports a
 * transient fault on a working system. This reports that the system will not
 * work for you at all until you do one thing. Hiding that leaves a person
 * looking at a product that silently does nothing — the state this was built
 * to end.
 *
 * English only — UI text policy.
 */
import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';
import { orgsApi, orgsKeys } from '../../lib/api/orgs';
import { vendorsApi, vendorsKeys } from '../../lib/api/vendors';

/**
 * The route that clears it. Must stay byte-identical to the gateway's
 * `EXECUTION_LANE_REFUSAL_ACTION` (`apps/chaos-gateway/src/org-auth.ts`) — that
 * constant is what an HTTP refusal carries, this is what an anticipating
 * screen links to, and a person who meets both must not be sent two places.
 *
 * Until 2026-08-02 the gateway's copy read `/settings?tab=vendors`, which
 * resolves nowhere: `/settings` is not a route (the G-6 consolidation turned
 * the fourteen `/settings/*` paths into redirects and never created the bare
 * parent) and the pane's tab id is `ai-signins`. Both were corrected together.
 */
export const AI_SIGNIN_ROUTE = '/settings-v2?tab=ai-signins';

/** Slower than the auth banner's 60 s: this state changes only when a human
 *  completes a sign-in, and that human is on the page it links to. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

export function ExecutionLaneBanner(): React.ReactElement | null {
  const statusQ = useQuery({
    queryKey: vendorsKeys.status(),
    queryFn: () => vendorsApi.status(),
    refetchInterval: POLL_INTERVAL_MS,
    // A failure here must not manufacture the banner. "We could not ask" is
    // not "you have no login", and asserting the second from the first would
    // put a permanent scold on the founder's screen the first time the
    // gateway hiccups.
    retry: false,
  });

  // ─── DO THEY STILL BELONG ANYWHERE? (2026-08-05) ────────────────────────
  //
  // The sentence below used to be unconditional: "Reading, boards and files are
  // open to you now." After the offboarding cascade removes somebody from their
  // last Workspace, the number of things open to them is zero, and this banner
  // was the only thing on their screen still speaking — cheerfully, and
  // falsely. Being removed and being told everything is available is worse than
  // being removed, because it costs the person an afternoon of believing the
  // product is broken before they think to ask a human.
  //
  // Same query KEY as ActiveOrgProvider's, so this is the cache it already
  // fills, not a second call — and no provider context is required, which
  // matters because this component is mounted standalone by its browser proof.
  // `retry: false` and the isSuccess check keep "we could not ask" from being
  // read as "you have nothing": an empty list is only believed when the server
  // actually said so.
  const orgsQ = useQuery({
    queryKey: orgsKeys.list(),
    queryFn: () => orgsApi.list(),
    staleTime: 60_000,
    retry: false,
  });
  const belongsNowhere = orgsQ.isSuccess && orgsQ.data.length === 0;

  // Nothing until we have an answer. A banner that flashes on every page load
  // and then vanishes is a banner people learn to ignore.
  if (!statusQ.data) return null;
  const rows = statusQ.data;
  if (rows.length === 0) return null;
  if (rows.some((v) => v.ready)) return null;

  // WHY there is no login, in the gateway's own vocabulary. `unattributed`
  // means the viewer resolved to no account at all — the state that used to
  // throw `execution_identity_unattributed` with nothing to click. Since
  // 2026-08-02 joining mints the account, so this branch should be unreachable
  // for anyone who arrived through either member door; it is kept, and worded
  // separately, because a person seeded before that change would otherwise be
  // told to connect a subscription into a tree that does not exist.
  //
  // ── AND IT WAS STILL TELLING THEM TO (2026-08-15) ───────────────────────
  //
  // The separate wording said "Open AI sign-ins and connect Claude or Codex —
  // THAT CREATES IT", and it does not. In production `chaos.accounts` rows are
  // minted in exactly one place — `org-invitations.ts::ensureExecutionAccount`,
  // inside the join transaction — and `lib/vendor-auth.ts` refuses ON PURPOSE
  // to create one from a spawn, calling it "a product act with billing meaning"
  // that belongs to onboarding. The sign-in screen has no path to it.
  //
  // The same fact is visible one field along in the payload this component
  // already reads: `getVendorStatuses()` returns `canLogIn: false` for exactly
  // this state, with its own note that offering the button "would be the same
  // reassuring lie one layer along". This banner rendered the button anyway.
  //
  // So the CTA is withheld here for the same reason it is withheld for
  // `belongsNowhere` directly below, and the sentence now names the step that
  // does work: an operator re-issuing the invitation. It agrees word for word
  // in substance with the gateway's `no_account` refusal
  // (`EXECUTION_LANE_REFUSAL_BY_REASON`, org-auth.ts) — one person, one fact,
  // two surfaces, and they must not differ.
  const unattributed = rows.every((v) => v.reason === 'unattributed');

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="execution-lane-banner"
      className={cn(
        'flex flex-col gap-2 border-b border-border bg-foreground/[0.03]',
        'px-3 py-2 text-sm text-foreground md:flex-row md:items-center md:px-4',
      )}
    >
      <Sparkles className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="font-medium" data-testid="execution-lane-banner-headline">
          {belongsNowhere
            ? 'You are not in any Workspace'
            : unattributed
              ? 'Agents cannot start for this sign-in yet'
              : "You're in — agents need your own AI sign-in"}
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {belongsNowhere
            ? 'Your access to every Workspace has been removed, so there are no boards, cards or files for you to open here. Ask whoever runs your Workspace to invite you again.'
            : unattributed
              ? 'This sign-in has no billing account of its own, so no agent can start for you. Connecting Claude or GPT will not create one — accounts are made when you join a Workspace. Ask whoever runs yours to send your invitation again.'
              : 'Reading, boards and files are open to you now. Starting an agent spends an AI subscription, and yours is not connected yet, so cards will not run.'}
        </div>
      </div>
      {/* A plain anchor, not a router <Link>: this component is rendered
          standalone by its browser proof, where no router context exists, and
          the same choice is already made for the same reason in
          ClaudeAuthBanner. A full navigation to an in-app path costs one
          reload and cannot throw. */}
      {/* No "connect a subscription" call to action for somebody who has
          nowhere to spend it — that route would open a settings pane belonging
          to no Workspace and teach them the product is broken twice. Nor for
          `unattributed`, where the pane opens but the door inside it is shut:
          the same payload this component reads reports `canLogIn: false` for
          that state, and connecting cannot create the account row anyway. */}
      {!belongsNowhere && !unattributed && (
      <a
        href={AI_SIGNIN_ROUTE}
        data-testid="execution-lane-banner-action"
        className={cn(
          'inline-flex shrink-0 items-center gap-1.5 self-start rounded border border-border',
          'bg-foreground/[0.04] px-2.5 py-1 text-xs font-medium no-underline',
          'hover:bg-foreground/[0.08] md:self-auto',
        )}
      >
        <Sparkles className="h-3 w-3" aria-hidden />
        Connect an AI sign-in
      </a>
      )}
    </div>
  );
}
